import { observer } from "mobx-react";
import { useLanguage, useTranslation } from "@/stores/RootStoreContext";
import { Box, VBox } from "@/components-v2/Box";
import { CommentActions, CommentListProps, CommentProps, EditCommentState } from "./commentsDomain";
import { toDate } from "@/utils/Dates";
import { Markdown } from "@/text/Markdown";
import { Dialog } from "@/components-v2/Dialog";
import { Button, LinkButton } from "@/components-v2/Button";

import { useEffect, useRef, useState } from "react";
import { MarkdownEditor } from "@/text/MarkdownEditor";
import { TextInput } from "@/components-v2/TextInput";
import { relativeTime } from "@/i18n/dateFormat";
import { LoadingIndicator } from "@/components-v2/LoadingIndicator";

export const CommentsView = observer((props: CommentListProps) => {
  const { t } = useTranslation();
  if (props.isLoading) {
    return <LoadingIndicator size={"sm"} />;
  }
  return (
    <VBox marginTop>
      <div className={"subtitle"}>{t("comments")}</div>
      {props.comments.map((comment) => (
        <CommentView key={comment.id} {...props} comment={comment} />
      ))}
      {!props.editState?.updateCommentId && <AddOrUpdateComment {...props} />}
    </VBox>
  );
});

export const CommentView = observer((props: CommentProps) => {
  const { t } = useTranslation();
  const locale = useLanguage();
  const { comment, actions, editState } = props;
  const { deleteCommentId, updateCommentId } = editState ?? {};
  return (
    <VBox gap={false} marginBottom={"lg"}>
      <Box alignItems="center">
        <div className={"font-semibold"}>{comment.commenterName}</div>
        <div className={"subtle"}>{t(relativeTime(toDate(comment.updatedAt), locale))}</div>
      </Box>
      {updateCommentId === comment.id ? (
        <AddOrUpdateComment {...props} />
      ) : (
        <Markdown value={comment.commentText} />
      )}
      {deleteCommentId === comment.id && (
        <Dialog
          maxWidth="sm"
          title={t("delete-comment")}
          onCancel={() => actions.onCancel?.()}
          onSubmit={() => actions.onConfirmDelete?.(deleteCommentId)}
          submitLabel={t("delete")}
        >
          <div>{t("delete-comment-description")}</div>
        </Dialog>
      )}
      {comment.isOwnComment && !updateCommentId && (
        <Box padding={false} gap="md">
          <LinkButton
            label={t("edit")}
            weight={"normal"}
            onClick={() => actions.onEditComment?.(comment)}
          />
          <LinkButton
            label={t("delete")}
            weight={"normal"}
            onClick={() => actions.onDeleteComment?.(comment.id)}
          />
        </Box>
      )}
    </VBox>
  );
});

export const AddOrUpdateComment = observer(
  ({
    decisionId,
    editState,
    actions: { onChange, onCancel, onSave, onToggleEditor },
  }: {
    decisionId?: string;
    editState?: EditCommentState;
    actions: CommentActions;
  }) => {
    const { t } = useTranslation();
    const buttonRef = useRef<HTMLButtonElement>(null);
    const [isPinned, setIsPinned] = useState(false);
    const { commentText, showEditor } = editState ?? {};

    useEffect(() => {
      if (showEditor) {
        buttonRef.current?.scrollIntoView();
      }
    }, [showEditor]);

    return showEditor ? (
      <VBox>
        <MarkdownEditor
          autofocus
          idleTimeout={20000}
          value={commentText ?? ""}
          onChange={(text) => onChange?.(text)}
        />
        <Box>
          <Button
            ref={buttonRef}
            variant={"primary"}
            label={t("save")}
            onClick={() => onSave?.(decisionId)}
          />
          <Button label={t("cancel")} variant={"secondary"} onClick={onCancel} />
        </Box>
      </VBox>
    ) : (
      <VBox
        sticky={"bottom"}
        onPinnedChange={setIsPinned}
        paddingY
        borderTop={isPinned}
        bg={isPinned ? "base" : undefined}
      >
        <TextInput placeholder={t("your-comment")} onFocus={onToggleEditor} />
      </VBox>
    );
  },
);
